// =============================================================================
// FILE: packages/renderer/src/components/license/LicenseGate.tsx
// PURPOSE: Full-screen overlay that appears when the 14-day trial has expired
//          and no BICSI ID has been verified.  Cannot be dismissed without a
//          valid ID.  Shows a countdown banner during the trial window.
// =============================================================================

import { useState, type JSX, type FormEvent } from 'react';
import { ShieldCheck, Lock, Clock, AlertTriangle, CreditCard, ArrowRight } from 'lucide-react';
import { RCD2EngineLockup } from '../branding/LogoMark.js';
import { useWorkspacePrefs } from '../../hooks/useWorkspacePrefs.js';
import type { LicenseStatus } from '../../hooks/useLicense.js';

interface Props {
  status:     LicenseStatus;
  onVerify:   (id: string) => Promise<{ ok: boolean; error?: string }>;
  onNavigate?: (route: string) => void;
}

export function LicenseGate({ status, onVerify, onNavigate }: Props): JSX.Element | null {
  // ── During trial — show a subtle banner only ──────────────────────────────
  if (status.status === 'trial') {
    return <TrialBanner daysRemaining={status.daysRemaining} onNavigate={onNavigate} />;
  }

  // ── Already verified — render nothing ─────────────────────────────────────
  if (status.status === 'verified') return null;

  // ── Expired — render the blocking overlay ─────────────────────────────────
  return <ExpiredOverlay onVerify={onVerify} onNavigate={onNavigate} />;
}

// ─── Trial banner (non-blocking) ──────────────────────────────────────────────
function TrialBanner({ daysRemaining, onNavigate }: { daysRemaining: number; onNavigate?: (r: string) => void }): JSX.Element {
  const [dismissed, setDismissed] = useState(false);
  const { prefs } = useWorkspacePrefs();
  // In Focus Mode the banner is suppressed so it never covers the Unfocus control.
  if (dismissed || prefs.focusMode) return <></>;

  return (
    <div
      data-testid="license-trial-banner"
      className="fixed top-0 left-0 right-0 z-[90] flex items-center justify-between px-4 py-1.5"
      style={{
        background:    '#1a2a1a',
        borderBottom:  '1px solid rgba(37,181,168,0.25)',
        backdropFilter:'blur(8px)',
      }}
    >
      <div className="flex items-center gap-2">
        <Clock className="h-3.5 w-3.5 text-teal-400" />
        <span className="text-[11px] text-teal-200 tracking-wide">
          <strong className="font-semibold">Trial mode</strong>
          {' '}— {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining.
        </span>
      </div>
      <div className="flex items-center gap-3">
        {onNavigate && (
          <button
            type="button"
            data-testid="trial-upgrade-btn"
            onClick={() => onNavigate('pricing')}
            className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-teal-300 hover:text-teal-100 transition"
          >
            <CreditCard className="h-3 w-3" />
            Choose a Plan
          </button>
        )}
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-200 transition"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

// ─── Expired overlay (blocking) ───────────────────────────────────────────────
function ExpiredOverlay({
  onVerify,
  onNavigate,
}: {
  onVerify:    (id: string) => Promise<{ ok: boolean; error?: string }>;
  onNavigate?: (route: string) => void;
}): JSX.Element {
  const [bicsiId,    setBicsiId]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [success,    setSuccess]    = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const res = await onVerify(bicsiId);
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(res.error ?? 'Verification failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="license-gate-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center"
      style={{
        background:     'rgba(7,12,18,0.96)',
        backdropFilter: 'blur(18px)',
      }}
    >
      <div
        className="relative w-full max-w-md mx-4 rounded-sm overflow-hidden"
        style={{
          border:     '1px solid rgba(37,181,168,0.3)',
          background: 'rgba(10,18,28,0.95)',
          boxShadow:  '0 0 60px rgba(37,181,168,0.12)',
        }}
      >
        {/* Header accent */}
        <div
          className="h-1 w-full"
          style={{ background: 'linear-gradient(90deg, #1B9480, #25B5A8, #7AE0D6)' }}
        />

        <div className="p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <RCD2EngineLockup height={36} />
          </div>

          {success ? (
            <SuccessState />
          ) : (
            <>
              <div className="flex items-center justify-center gap-3 mb-5">
                <div
                  className="flex items-center justify-center w-10 h-10 rounded-sm"
                  style={{
                    background: 'rgba(251,191,36,0.08)',
                    border:     '1px solid rgba(251,191,36,0.3)',
                  }}
                >
                  <Lock className="h-5 w-5 text-amber-400" />
                </div>
                <div>
                  <div className="text-amber-200 font-semibold text-[15px]">
                    Trial Period Expired
                  </div>
                  <div className="text-zinc-500 text-[11px] tracking-wide mt-0.5">
                    Verification required to continue
                  </div>
                </div>
              </div>

              <p className="text-zinc-400 text-[12.5px] leading-relaxed mb-5 text-center">
                Enter your <strong className="text-teal-300">BICSI member number</strong> or{' '}
                <strong className="text-teal-300">RCDD designation</strong> to unlock full access.
                This is a one-time step — your credential is stored locally and never transmitted.
              </p>

              <form onSubmit={(e) => { void handleSubmit(e); }}>
                <div className="mb-3">
                  <label className="block text-[10px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">
                    BICSI ID / RCDD Number
                  </label>
                  <input
                    data-testid="bicsi-id-input"
                    type="text"
                    value={bicsiId}
                    onChange={(e) => setBicsiId(e.target.value)}
                    placeholder="e.g.  RCDD-12345  or  987654"
                    autoFocus
                    className="w-full bg-zinc-900/80 border border-zinc-700 rounded-sm px-3 py-2.5 text-zinc-100 text-[13px] font-mono placeholder-zinc-600 focus:border-teal-400/60 focus:outline-none focus:ring-1 focus:ring-teal-400/20 transition"
                  />
                </div>

                {error && (
                  <div
                    data-testid="license-gate-error"
                    className="flex items-center gap-2 mb-3 px-3 py-2 rounded-sm text-[11px] text-amber-300"
                    style={{
                      background: 'rgba(251,191,36,0.07)',
                      border:     '1px solid rgba(251,191,36,0.25)',
                    }}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {error}
                  </div>
                )}

                <button
                  data-testid="license-verify-btn"
                  type="submit"
                  disabled={submitting || !bicsiId.trim()}
                  className="w-full py-2.5 rounded-sm font-semibold text-[13px] tracking-wide transition disabled:opacity-40"
                  style={{
                    background: submitting ? 'rgba(37,181,168,0.5)' : 'rgba(37,181,168,0.9)',
                    color:      '#071218',
                  }}
                >
                  {submitting ? 'Verifying…' : 'Verify & Unlock'}
                </button>
              </form>

              <p className="mt-4 text-center text-[10.5px] text-zinc-600 leading-relaxed">
                Don't have a BICSI ID? Visit{' '}
                <button
                  type="button"
                  className="text-teal-500 hover:underline"
                  onClick={() => window.open('https://www.bicsi.org', '_blank', 'noreferrer')}
                >
                  bicsi.org
                </button>{' '}
                to become a member.
              </p>

              {/* Divider */}
              <div className="mt-6 mb-4 flex items-center gap-3">
                <div className="flex-1 h-px bg-zinc-800" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-600">or</span>
                <div className="flex-1 h-px bg-zinc-800" />
              </div>

              {/* Purchase CTA */}
              {onNavigate && (
                <button
                  type="button"
                  data-testid="license-buy-plan-btn"
                  onClick={() => onNavigate('pricing')}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-sm text-[12.5px] font-semibold tracking-wide transition"
                  style={{
                    background: 'rgba(252,211,77,0.08)',
                    border:     '1px solid rgba(252,211,77,0.3)',
                    color:      '#FCD34D',
                  }}
                  onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(252,211,77,0.14)'; }}
                  onMouseOut={(e)  => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(252,211,77,0.08)'; }}
                >
                  <CreditCard className="h-3.5 w-3.5" />
                  View Pricing &amp; Buy a License
                  <ArrowRight className="h-3.5 w-3.5 opacity-70" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SuccessState(): JSX.Element {
  return (
    <div className="flex flex-col items-center gap-4 py-4" data-testid="license-gate-success">
      <div
        className="flex items-center justify-center w-14 h-14 rounded-full"
        style={{
          background: 'rgba(37,181,168,0.12)',
          border:     '2px solid rgba(37,181,168,0.5)',
          boxShadow:  '0 0 24px rgba(37,181,168,0.25)',
        }}
      >
        <ShieldCheck className="h-7 w-7 text-teal-300" />
      </div>
      <div className="text-center">
        <div className="text-teal-200 font-semibold text-[16px] mb-1">
          Access Verified
        </div>
        <div className="text-zinc-400 text-[12px] leading-relaxed">
          Your BICSI credential has been saved locally.
          <br />
          The RCD² Engine is now fully unlocked.
        </div>
      </div>
      <div className="text-zinc-600 text-[10.5px] animate-pulse">
        Refreshing in a moment…
      </div>
    </div>
  );
}
